import React, { useEffect } from 'react';

/**
 * Clean, parent-appropriate printable view of a single report-card narrative.
 *
 * PRIVACY-CRITICAL: this component receives ONLY the parent-facing narrative
 * body plus header context (student name, class, school year, report period,
 * date). It deliberately accepts no draft object, no feedback, no model/token
 * metadata, and — most importantly — NO teacher flags. The private teacher
 * flags live in a separate `narrativeTeacherFlags` subcollection that the
 * Results overview never even queries, so there is no code path by which they
 * could reach this component. Keep it that way: do not add a `draft` or `flags`
 * prop here. If you need more on the printout, pass an explicit, parent-safe
 * scalar prop and render only that.
 *
 * The on-screen chrome (toolbar) is marked `.no-print`; the global print
 * stylesheet plus the local @media print block below collapse everything except
 * the narrative + header so the browser's "Save as PDF" / print produces a
 * clean one-pager.
 */
function ReportCardPrintView({ studentName, studentClass, schoolYear, periodLabel, narrativeText, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const printedOn = new Date().toLocaleDateString();

  return (
    <div className="rcpv-overlay" onClick={onClose}>
      <div className="rcpv-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="rcpv-toolbar no-print">
          <button className="rcpv-btn rcpv-print" onClick={() => window.print()}>🖨️ Print / Save as PDF</button>
          <button className="rcpv-btn rcpv-close" onClick={onClose}>Close</button>
        </div>

        <div className="rcpv-document">
          <div className="rcpv-header">
            <h1 className="rcpv-title">Report Card Narrative</h1>
            <div className="rcpv-meta">
              <div><strong>Student:</strong> {studentName || '—'}</div>
              <div><strong>Class:</strong> {studentClass || '—'}</div>
              <div><strong>School year:</strong> {schoolYear || '—'}</div>
              <div><strong>Report period:</strong> {periodLabel || '—'}</div>
              <div><strong>Printed:</strong> {printedOn}</div>
            </div>
          </div>
          <div className="rcpv-body">{narrativeText || ''}</div>
        </div>
      </div>

      <style>{`
        .rcpv-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          z-index: 1100;
          padding: 24px;
          overflow-y: auto;
        }
        .rcpv-sheet {
          background: #fff;
          border-radius: 8px;
          max-width: 720px;
          width: 100%;
          padding: 22px 26px 30px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
        }
        .rcpv-toolbar {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-bottom: 18px;
        }
        .rcpv-btn {
          padding: 8px 14px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.92em;
          font-weight: 600;
        }
        .rcpv-print { background: #3b82f6; color: #fff; }
        .rcpv-close { background: #e5e7eb; color: #222; }
        .rcpv-header { border-bottom: 2px solid #333; padding-bottom: 12px; margin-bottom: 18px; }
        .rcpv-title { font-size: 1.4em; margin: 0 0 10px; }
        .rcpv-meta { font-size: 0.92em; color: #333; line-height: 1.7; }
        .rcpv-body {
          white-space: pre-wrap;
          line-height: 1.6;
          font-size: 1em;
          color: #1f2937;
        }

        @media print {
          .no-print { display: none !important; }
          .rcpv-overlay {
            position: absolute !important;
            inset: 0;
            background: #fff;
            padding: 0;
            overflow: visible;
            display: block;
          }
          .rcpv-sheet {
            box-shadow: none;
            border-radius: 0;
            max-width: none;
            width: auto;
            padding: 0;
          }
          .rcpv-body { font-size: 12pt; }
          @page { size: portrait; margin: 0.75in; }
        }
      `}</style>
    </div>
  );
}

export default ReportCardPrintView;
